/**
 * Browser-native notifications for buyer ↔ vendor messaging.
 *
 * Companions to the in-app `<ToastProvider>`: toasts cover the visible-tab
 * case; `Notification` covers the hidden-tab case (other tab, minimised
 * window, different desktop). We deliberately do NOT register a service
 * worker — without push subscriptions there is nothing useful a SW can do
 * here, and the operator has no PWA at the moment. This means delivery
 * still requires AT LEAST ONE tab of the app to be open.
 *
 * Permission flow:
 *   - We never call `Notification.requestPermission()` on app load. Browsers
 *     permanently downgrade auto-prompted permissions to `denied`. The user
 *     must opt in via `<BrowserNotificationsToggle>` (a real user gesture).
 *   - Permission grant is global per origin; we layer a per-app boolean in
 *     `localStorage` so the user can toggle off without revoking at the OS.
 *
 * Multi-tab dedupe:
 *   - Each `notifyBrowser` call broadcasts on a `BroadcastChannel`. Other
 *     tabs see the broadcast and refuse to re-fire the same `(tag, key)`
 *     within `DEDUPE_WINDOW_MS`. Without this, a user with the inbox open in
 *     three tabs would get three OS notifications per message.
 *   - `localStorage` fallback covers browsers without `BroadcastChannel`
 *     (Safari < 15.4, etc.).
 */

import { useCallback, useEffect, useState } from 'react';

const USER_PREF_KEY = 'pc.shop.browserNotifications.enabled';
const DEDUPE_LOCAL_KEY_PREFIX = 'pc.shop.browserNotifications.lastFired:';
const DEDUPE_WINDOW_MS = 5_000;
const BROADCAST_CHANNEL_NAME = 'pc.shop.browserNotifications';

export type BrowserNotificationsUserPref = 'enabled' | 'disabled' | 'unset';

export interface BrowserNotificationsState {
  /** Whether `window.Notification` exists in this environment. */
  supported: boolean;
  /**
   * Native permission. `'unsupported'` is returned for SSR / older Safari /
   * any environment without the API.
   */
  permission: NotificationPermission | 'unsupported';
  /** App-level user toggle, independent of OS permission. */
  userPref: BrowserNotificationsUserPref;
  /** Convenience: should we actually surface notifications right now? */
  isActive: boolean;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function notificationSupported(): boolean {
  return isBrowser() && typeof window.Notification !== 'undefined';
}

export function getBrowserNotificationsState(): BrowserNotificationsState {
  if (!notificationSupported()) {
    return {
      supported: false,
      permission: 'unsupported',
      userPref: readUserPref(),
      isActive: false,
    };
  }
  const permission = window.Notification.permission;
  const userPref = readUserPref();
  const isActive = permission === 'granted' && userPref !== 'disabled';
  return { supported: true, permission, userPref, isActive };
}

function readUserPref(): BrowserNotificationsUserPref {
  if (!isBrowser()) return 'unset';
  try {
    const v = window.localStorage.getItem(USER_PREF_KEY);
    if (v === 'enabled' || v === 'disabled') return v;
  } catch {
    // Private mode / disabled storage: default to unset.
  }
  return 'unset';
}

function writeUserPref(v: BrowserNotificationsUserPref): void {
  if (!isBrowser()) return;
  try {
    if (v === 'unset') window.localStorage.removeItem(USER_PREF_KEY);
    else window.localStorage.setItem(USER_PREF_KEY, v);
  } catch {
    // Ignore — UI degrades to no persistence, not a crash.
  }
  emitStateChange();
}

export function setBrowserNotificationsUserPref(
  v: BrowserNotificationsUserPref,
): void {
  writeUserPref(v);
}

/**
 * Prompt the OS for permission. Must be called from a real user gesture
 * (button click). Returns the resulting permission.
 *
 * On grant we also flip the user preference to `'enabled'` so the toggle
 * reflects an active state without a second click.
 */
export async function requestBrowserNotificationsPermission(): Promise<
  NotificationPermission | 'unsupported'
> {
  if (!notificationSupported()) return 'unsupported';
  let result: NotificationPermission;
  try {
    // Older Safari uses the callback form; modern browsers return a Promise.
    const maybe = window.Notification.requestPermission();
    result = maybe instanceof Promise ? await maybe : await new Promise(
      (resolve) => window.Notification.requestPermission(resolve),
    );
  } catch {
    return window.Notification.permission;
  }
  if (result === 'granted' && readUserPref() !== 'disabled') {
    writeUserPref('enabled');
  }
  emitStateChange();
  return result;
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

interface NotifyPayload {
  /**
   * Stable key for OS-level replacement. Repeated notifications with the same
   * `tag` collapse into one tray entry. We use the thread id for messaging.
   */
  tag: string;
  /**
   * Per-event identity used for dedupe. Two notifications for the same `tag`
   * + `key` within {@link DEDUPE_WINDOW_MS} only fire once across all open
   * tabs. For shop messages we pass `last_message_at`.
   */
  key: string;
  title: string;
  body: string;
  /** Optional URL/path for asset icon (favicon). */
  icon?: string;
  /** Click handler — receives the focused window so we can route. */
  onClick?: () => void;
}

interface BroadcastFiredMessage {
  type: 'fired';
  tag: string;
  key: string;
  at: number;
}

let broadcastChannel: BroadcastChannel | null = null;
const recentFires = new Map<string, number>();

function dedupeKey(tag: string, key: string): string {
  return `${tag}::${key}`;
}

function ensureBroadcastChannel(): BroadcastChannel | null {
  if (!isBrowser()) return null;
  if (typeof BroadcastChannel === 'undefined') return null;
  if (broadcastChannel) return broadcastChannel;
  try {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannel.addEventListener('message', (ev) => {
      const data = ev.data as BroadcastFiredMessage | undefined;
      if (!data || data.type !== 'fired') return;
      recentFires.set(dedupeKey(data.tag, data.key), data.at);
    });
  } catch {
    broadcastChannel = null;
  }
  return broadcastChannel;
}

function recentlyFired(tag: string, key: string): boolean {
  const k = dedupeKey(tag, key);
  const now = Date.now();
  const local = recentFires.get(k);
  if (local && now - local < DEDUPE_WINDOW_MS) return true;
  if (isBrowser()) {
    try {
      const raw = window.localStorage.getItem(DEDUPE_LOCAL_KEY_PREFIX + k);
      if (raw) {
        const at = Number(raw);
        if (Number.isFinite(at) && now - at < DEDUPE_WINDOW_MS) return true;
      }
    } catch {
      // Ignore.
    }
  }
  return false;
}

function recordFire(tag: string, key: string): void {
  const k = dedupeKey(tag, key);
  const at = Date.now();
  recentFires.set(k, at);
  if (isBrowser()) {
    try {
      window.localStorage.setItem(DEDUPE_LOCAL_KEY_PREFIX + k, String(at));
    } catch {
      // Ignore.
    }
  }
  ensureBroadcastChannel()?.postMessage({
    type: 'fired',
    tag,
    key,
    at,
  } satisfies BroadcastFiredMessage);
}

/**
 * Fire a system notification if the user has opted in and granted permission.
 * Returns `true` when a notification was actually displayed.
 *
 * The caller is responsible for deciding *when* to fire (visible vs hidden
 * tab, in-thread vs out-of-thread). This module only handles permission
 * gating, multi-tab dedupe, and OS-level mechanics.
 */
export function notifyBrowser(payload: NotifyPayload): boolean {
  if (!notificationSupported()) return false;
  const state = getBrowserNotificationsState();
  if (!state.isActive) return false;
  if (recentlyFired(payload.tag, payload.key)) return false;

  let n: Notification;
  try {
    n = new window.Notification(payload.title, {
      body: payload.body,
      tag: payload.tag,
      icon: payload.icon,
      // `renotify: true` would force the OS to alert again on tag replacement.
      // We rely on dedupe instead — repeated messages should collapse quietly.
    });
  } catch {
    // Construction can throw on some platforms (eg. iOS Safari without PWA).
    return false;
  }

  recordFire(payload.tag, payload.key);

  if (payload.onClick) {
    n.onclick = (ev) => {
      ev.preventDefault();
      try {
        window.focus();
      } catch {
        // Ignore — focusing across windows can fail in some browsers.
      }
      payload.onClick?.();
      n.close();
    };
  }
  return true;
}

// ---------------------------------------------------------------------------
// React glue
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Set<Listener>();

function emitStateChange(): void {
  for (const l of listeners) l();
}

function subscribeStateChange(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

if (isBrowser()) {
  // The OS permission can change in browser settings without firing any web
  // event. Re-poll on visibility / focus so the toggle UI stays accurate.
  document.addEventListener('visibilitychange', emitStateChange);
  window.addEventListener('focus', emitStateChange);
}

export interface UseBrowserNotificationsState extends BrowserNotificationsState {
  request: () => Promise<NotificationPermission | 'unsupported'>;
  setUserPref: (v: BrowserNotificationsUserPref) => void;
}

export function useBrowserNotificationsState(): UseBrowserNotificationsState {
  const [state, setState] = useState<BrowserNotificationsState>(() =>
    getBrowserNotificationsState(),
  );

  useEffect(() => {
    const refresh = () => setState(getBrowserNotificationsState());
    refresh();
    return subscribeStateChange(refresh);
  }, []);

  const request = useCallback(async () => {
    const r = await requestBrowserNotificationsPermission();
    setState(getBrowserNotificationsState());
    return r;
  }, []);

  const setUserPref = useCallback((v: BrowserNotificationsUserPref) => {
    setBrowserNotificationsUserPref(v);
    setState(getBrowserNotificationsState());
  }, []);

  return { ...state, request, setUserPref };
}
